import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const SALT_LEN = 16;
const IV_LEN = 16;
const TAG_LEN = 16;
const KEY_LEN = 32;
const PBKDF2_ITERATIONS = 100000;

async function decryptFile(encrypted: ArrayBuffer, password: string): Promise<string> {
  const enc = new Uint8Array(encrypted);
  if (enc.length < SALT_LEN + IV_LEN + TAG_LEN) {
    throw new Error('Encrypted data too short');
  }
  const salt = enc.subarray(0, SALT_LEN);
  const iv = enc.subarray(SALT_LEN, SALT_LEN + IV_LEN);
  const tag = enc.subarray(SALT_LEN + IV_LEN, SALT_LEN + IV_LEN + TAG_LEN);
  const ciphertext = enc.subarray(SALT_LEN + IV_LEN + TAG_LEN);

  const encoder = new TextEncoder();
  const passwordKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveBits']
  );

  const keyMaterial = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    passwordKey,
    KEY_LEN * 8
  );

  const key = await crypto.subtle.importKey(
    'raw',
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt']
  );

  const ciphertextWithTag = new Uint8Array(ciphertext.length + TAG_LEN);
  ciphertextWithTag.set(ciphertext);
  ciphertextWithTag.set(tag, ciphertext.length);

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv, tagLength: TAG_LEN * 8 },
    key,
    ciphertextWithTag
  );

  return new TextDecoder('utf-8').decode(plaintext);
}

function triggerDownload(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const DecryptOrdersReportPage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [decrypting, setDecrypting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setError('');
    setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !password) {
      setError('Please select a file and enter the password.');
      return;
    }
    setDecrypting(true);
    setError('');
    setSuccess(false);

    try {
      const buffer = await file.arrayBuffer();
      const plaintext = await decryptFile(buffer, password);
      const outFilename = file.name.replace(/\.enc$/i, '') || 'orders-decrypted.csv';
      triggerDownload(plaintext, outFilename);
      setSuccess(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Decryption failed';
      setError('Invalid password or corrupt file. Please try again.');
      console.error('[DecryptOrdersReport]', msg);
    } finally {
      setDecrypting(false);
    }
  };

  return (
    <div style={{ maxWidth: '600px', margin: '2rem auto', padding: '2rem' }}>
      <Link to="/" className="sr-only">Return to Alert1 Home</Link>
      <h1>Decrypt Orders Report</h1>
      <p style={{ color: '#666', marginBottom: '2rem' }}>
        Upload the encrypted orders report file (.csv.enc) and enter the password from the separate decryption email to download the decrypted CSV.
      </p>

      <form
        onSubmit={handleSubmit}
        style={{ background: '#f8f9fa', padding: '2rem', borderRadius: '8px' }}
      >
        <div style={{ marginBottom: '1.5rem' }}>
          <label
            htmlFor="file"
            style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}
          >
            Encrypted file
          </label>
          <input
            type="file"
            id="file"
            accept=".enc,*/*"
            onChange={handleFileChange}
            required
            style={{ width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid #ddd' }}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label
            htmlFor="password"
            style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}
          >
            Password
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError('');
            }}
            required
            placeholder="Enter password from decryption email"
            style={{ width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid #ddd' }}
          />
        </div>

        {error && (
          <div
            style={{
              marginBottom: '1rem',
              padding: '0.75rem',
              background: '#f8d7da',
              borderRadius: '4px',
              color: '#721c24',
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            style={{
              marginBottom: '1rem',
              padding: '0.75rem',
              background: '#d4edda',
              borderRadius: '4px',
              color: '#155724',
            }}
          >
            File decrypted and downloaded successfully.
          </div>
        )}

        <button
          type="submit"
          disabled={decrypting || !file || !password}
          className="btn btn-primary"
          style={{ width: '100%' }}
        >
          {decrypting ? 'Decrypting…' : 'Decrypt & Download'}
        </button>
      </form>

      <p style={{ marginTop: '1.5rem', fontSize: '0.9rem', color: '#666' }}>
        Decryption happens in your browser. Your file and password are never sent to our servers.
      </p>
    </div>
  );
};

export default DecryptOrdersReportPage;
